import React, { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { useSharedValue, withTiming } from 'react-native-reanimated';
import SelectorControllableReanimated, { DEFAULT_SPACE_BETWEEN, } from 'design-system/SelectorControllableReanimated';
import { rem } from 'design-system/values';
const SELECTION_STEP = 1;
const SPACE_BETWEEN = DEFAULT_SPACE_BETWEEN;
const DatesSegmentedControl = ({ selectedTabIndex, setSelectedTabIndex, }) => {
    const scrollRef = useRef(null);
    const scrollX = useSharedValue(selectedTabIndex * SELECTION_STEP);
    const [viewportWidth, setViewportWidth] = useState(0);
    const headerPadding = rem(16);
    const sections = useMemo(() => [
        { name: 'Weekly', width: rem(84) },
        { name: 'Monthly', width: rem(91) },
        { name: 'Quarterly', width: rem(99) },
        { name: 'Yearly', width: rem(76) },
        { name: 'Custom', width: rem(88) },
    ].map((item) => ({
        ...item,
        id: item.name,
        text: item.name,
    })), []);
    const offsets = useMemo(() => sections.map((_, index) => sections
        .slice(0, index)
        .reduce((sum, s) => sum + s.width + SPACE_BETWEEN, 0)), [sections]);
    const selectorWidth = useMemo(() => sections.reduce((sum, s) => sum + s.width, 0) +
        SPACE_BETWEEN * Math.max(sections.length - 1, 0), [sections]);
    const setSelection = useCallback((index) => {
        setSelectedTabIndex(index);
    }, [setSelectedTabIndex]);
    const getScrollX = useCallback((index) => offsets[index] +
        (sections[index]?.width ?? 0) / 2 -
        (viewportWidth / 2 - headerPadding), [headerPadding, offsets, sections, viewportWidth]);
    // Drive the pill and centre the active segment (incl. external changes).
    // The first centring waits for the viewport width and jumps there, so the
    // modal opens on the selected segment instead of sliding to it.
    const didInitialScroll = useRef(false);
    useEffect(() => {
        if (!viewportWidth) {
            return;
        }
        const animated = didInitialScroll.current;
        didInitialScroll.current = true;
        scrollX.value = animated
            ? withTiming(selectedTabIndex * SELECTION_STEP)
            : selectedTabIndex * SELECTION_STEP;
        scrollRef.current?.scrollTo({
            x: getScrollX(selectedTabIndex),
            animated,
        });
    }, [getScrollX, scrollX, selectedTabIndex, viewportWidth]);
    const onLayout = useCallback((e) => {
        setViewportWidth(e.nativeEvent.layout.width);
    }, []);
    const selectorWidthStyle = useMemo(() => ({ width: selectorWidth }), [selectorWidth]);
    return (<ScrollView horizontal showsHorizontalScrollIndicator={false} ref={scrollRef} onLayout={onLayout} contentContainerStyle={styles.contentContainer}>
      <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={SELECTION_STEP} setSelection={setSelection} style={selectorWidthStyle} itemContainerStyle={styles.itemContainerStyle} selectorStyle={styles.selectorStyle}/>
    </ScrollView>);
};
export default memo(DatesSegmentedControl);
const styles = StyleSheet.create({
    contentContainer: {
        paddingHorizontal: rem(16),
    },
    itemContainerStyle: {
        padding: 0,
        margin: 0,
    },
    selectorStyle: {
        borderRadius: rem(8),
        justifyContent: 'center',
        padding: 0,
        margin: 0,
    },
});
